import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Users,
  ChevronLeft,
  ChevronRight,
  MapPin,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Loading } from '../../components/ui/Loading';

interface ScheduledClass {
  id: string;
  name: string;
  date: string;
  time: string;
  endTime?: string;
  enrolled: Record<string, any>;
  maxCapacity: number;
  location?: string;
  classTypeId?: string;
  color?: string;
}

const InstructorClasses: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<ScheduledClass[]>([]);
  const [instructorId, setInstructorId] = useState<string | null>(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorId();
    }
  }, [businessId, currentUser]);

  useEffect(() => {
    if (instructorId) {
      loadClasses();
    }
  }, [instructorId, currentWeekStart]);

  const loadInstructorId = async () => {
    if (!businessId || !currentUser) return;

    try {
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const snapshot = await getDocs(instructorsQuery);

      if (!snapshot.empty) {
        setInstructorId(snapshot.docs[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error cargando instructor:', error);
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    if (!businessId || !instructorId) return;

    try {
      setLoading(true);

      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('instructorId', '==', instructorId),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(classesQuery);

      const allClasses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ScheduledClass[];

      // Filtrar clases de la semana actual
      const weekClasses = allClasses.filter(c => {
        const classDate = new Date(c.date + 'T00:00:00');
        return classDate >= currentWeekStart && classDate <= weekEnd;
      }).sort((a, b) => {
        if (a.date === b.date) return a.time.localeCompare(b.time);
        return a.date.localeCompare(b.date);
      });

      setClasses(weekClasses);
    } catch (error) {
      console.error('Error cargando clases:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const formatWeekRange = () => {
    const end = new Date(currentWeekStart);
    end.setDate(end.getDate() + 6);
    const startStr = currentWeekStart.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    const endStr = end.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    return `${startStr} - ${endStr}`;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getClassesForDay = (date: Date) => {
    // Formato local YYYY-MM-DD sin conversión a UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return classes.filter(c => c.date === dateStr);
  };

  const formatTime = (time: string) => time.substring(0, 5);

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const weekDays = getWeekDays();

  if (!instructorId && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Cuenta no vinculada</h2>
          <p className="text-gray-400">
            Tu cuenta no está vinculada a un perfil de instructor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Mis Clases</h1>
        <p className="text-gray-400">Vista semanal de tu agenda</p>
      </div>

      {/* Navegación de semanas */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigateWeek('prev')}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Anterior
        </Button>
        <div className="text-center">
          <button
            onClick={goToToday}
            className="text-lg font-semibold text-white hover:text-primary-400 transition-colors"
          >
            {formatWeekRange()}
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigateWeek('next')}>
          Siguiente
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      ) : (
        /* Calendario semanal */
        <div className="space-y-4">
          {weekDays.map((day, index) => {
            const dayClasses = getClassesForDay(day);
            const today = isToday(day);
            const isPast = day < new Date() && !today;

            return (
              <div key={index} className={`${isPast ? 'opacity-50' : ''}`}>
                {/* Cabecera del día */}
                <div className={`flex items-center gap-3 mb-2 ${today ? 'text-primary-400' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center ${
                    today ? 'bg-primary-500 text-white' : 'bg-gray-800'
                  }`}>
                    <span className="text-xs font-medium">{dayNames[index]}</span>
                    <span className="text-sm font-bold">{day.getDate()}</span>
                  </div>
                  <span className="font-medium">
                    {day.toLocaleDateString('es-ES', { weekday: 'long' })}
                  </span>
                  {today && (
                    <span className="px-2 py-0.5 bg-primary-500/20 text-primary-400 text-xs font-medium rounded-full">
                      HOY
                    </span>
                  )}
                </div>

                {/* Clases del día */}
                {dayClasses.length === 0 ? (
                  <Card className="p-4 border-dashed opacity-50">
                    <p className="text-sm text-gray-500 text-center">Sin clases programadas</p>
                  </Card>
                ) : (
                  <div className="space-y-2 ml-12">
                    {dayClasses.map((classItem) => {
                      const enrolledCount = Object.keys(classItem.enrolled || {}).length;
                      const isFull = enrolledCount >= classItem.maxCapacity;

                      return (
                        <Card
                          key={classItem.id}
                          className="p-3 cursor-pointer hover:border-primary-500/50 transition-all"
                          onClick={() => navigate(`/instructor/class/${classItem.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-1 h-12 rounded-full bg-primary-500" />
                              <div>
                                <h3 className="font-semibold text-white">{classItem.name}</h3>
                                <div className="flex items-center gap-3 text-sm text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatTime(classItem.time)}
                                    {classItem.endTime && ` - ${formatTime(classItem.endTime)}`}
                                  </span>
                                  {classItem.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {classItem.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                                isFull 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-green-500/20 text-green-400'
                              }`}>
                                <Users className="w-3 h-3" />
                                {enrolledCount}/{classItem.maxCapacity}
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Resumen */}
      <Card className="p-4 bg-gray-800/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Total esta semana:</span>
          <span className="font-semibold text-white">{classes.length} clases</span>
        </div>
      </Card>
    </div>
  );
};

export default InstructorClasses;










